import React, { useContext } from 'react';
import { isExternallyBilled } from '@leedpulse-monorepo/enums';
import { useUsersPageController } from '../controllers/useUsersPageController';
import AddUserModal from '../../../components/users/modals/add-user/AddUserModal';
import ViewProfileModal from '../../../components/users/modals/view-profile/ViewProfileModal';
import ReactivateUserModal from '../../../components/users/modals/reactivate-user/ReactivateUserModal';
import ChangeNumberModal from '../../../components/users/modals/change-number/ChangeNumberModal';
import DeactivateUserModal from '../../../components/users/modals/deactivate-user/DeactivateUserModal';
import DeleteUserModal from '../../../components/users/modals/delete-user/DeleteUserModal';
import EditUserModal from '../../../components/users/modals/edit-user/EditUserModal';
import CompanyContext from '../../../contexts/company/CompanyContext';

interface UsersPageModalsProps {
  state: ReturnType<typeof useUsersPageController>['state'];
  actions: ReturnType<typeof useUsersPageController>['actions'];
}

export const UsersPageModals: React.FC<UsersPageModalsProps> = ({ state, actions }) => {
  const { hasValidPayment, activeCompany } = useContext(CompanyContext);
  const isCompanyExternallyBilled = isExternallyBilled(activeCompany?.partner_system, activeCompany?.id_stripe);
  const {
    activeModal,
    operationLoading,
    selectedUser,
    availableNumbers,
    allNumbers,
    transferQuestion,
    isAssigning,
  } = state;
  const {
    closeModal,
    handleAddUser,
    handleConfirmReactivate,
    handleConfirmChangeNumber,
    answerContactsTransfer,
    handleConfirmDeactivate,
    handleConfirmDelete,
    handleSubmitEdit,
    openModal,
  } = actions;

  return (
    <>
      <AddUserModal
        isOpen={activeModal === 'ADD_USER'}
        isLoading={operationLoading}
        hasValidPayment={hasValidPayment}
        isCompanyExternallyBilled={isCompanyExternallyBilled}
        companyId={activeCompany?.id}
        partnerSystem={activeCompany?.partner_system === 'apimo' ? activeCompany.partner_system : null}
        onCancel={closeModal}
        onSubmit={handleAddUser} // TODO : @MrWarzo reset la modal au submit/close
        availableNumbers={availableNumbers}
        transferQuestion={transferQuestion}
        isAssigning={isAssigning}
        onAnswerTransfer={answerContactsTransfer}
      />

      <ViewProfileModal
        isOpen={activeModal === 'VIEW_PROFILE'}
        user={selectedUser}
        onClose={closeModal}
        onEdit={() => selectedUser && openModal('EDIT_USER', selectedUser)}
        onReactivate={() => selectedUser && openModal('REACTIVATE_USER', selectedUser)}
        onChangeNumber={() => selectedUser && openModal('CHANGE_NUMBER', selectedUser)}
        onDeactivate={() => selectedUser && openModal('DEACTIVATE_USER', selectedUser)}
      />

      <ReactivateUserModal
        isOpen={activeModal === 'REACTIVATE_USER'}
        user={selectedUser}
        isLoading={operationLoading}
        onCancel={closeModal}
        onConfirm={handleConfirmReactivate}
      />

      <ChangeNumberModal
        isOpen={activeModal === 'CHANGE_NUMBER'}
        user={selectedUser}
        isLoading={operationLoading}
        allNumbers={allNumbers}
        transferQuestion={transferQuestion}
        isAssigning={isAssigning}
        onCancel={closeModal}
        onConfirm={handleConfirmChangeNumber}
        onAnswerTransfer={answerContactsTransfer}
      />

      <DeactivateUserModal
        isOpen={activeModal === 'DEACTIVATE_USER'}
        user={selectedUser}
        isLoading={operationLoading}
        transferQuestion={transferQuestion}
        isAssigning={isAssigning}
        onCancel={closeModal}
        onConfirm={handleConfirmDeactivate}
        onAnswerTransfer={answerContactsTransfer}
      />

      <DeleteUserModal
        isOpen={activeModal === 'DELETE_USER'}
        user={selectedUser}
        isLoading={operationLoading}
        onCancel={closeModal}
        onConfirm={handleConfirmDelete}
      />

      <EditUserModal
        isOpen={activeModal === 'EDIT_USER'}
        user={selectedUser}
        isLoading={operationLoading}
        onCancel={closeModal}
        onSubmit={handleSubmitEdit}
      />
    </>
  );
};
